// Copyright © 2026 Gian Marco Cappelli — SAPIENS (finsapiens.it). Tutti i diritti riservati. Sviluppato su commissione esclusiva. Vietata la riproduzione senza autorizzazione scritta.
import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';

// P8 — 404 ottimizzato (Deployment 13)
// • Mostra 3 calcolatori più popolari come scorciatoia
// • Invia evento GA4 `page_not_found` SOLO se il consenso cookie è attivo
const TOP_CALCULATORS = [
  { slug: 'mutuo', label: 'Mutuo', desc: 'Rata, interessi e ammortamento' },
  { slug: 'stipendio-netto', label: 'Stipendio netto', desc: 'Da RAL al netto in busta paga' },
  { slug: 'pac', label: 'PAC', desc: 'Piano di accumulo capitale' },
];

const NotFoundPage = () => {
  const location = useLocation();

  useEffect(() => {
    // Fire GA4 event ONLY if user has given analytics consent
    try {
      const consent = (() => {
        try {
          const raw = localStorage.getItem('cookie-consent');
          if (!raw) return null;
          return JSON.parse(raw);
        } catch {
          return null;
        }
      })();
      const allowed = consent && (consent.analytics === true || consent === 'all');
      if (allowed && typeof window.gtag === 'function') {
        window.gtag('event', 'page_not_found', {
          page_path: location.pathname,
          page_location: window.location.href,
        });
      }
    } catch {
      /* never break the 404 page */
    }
  }, [location.pathname]);

  return (
    <div
      className="min-h-screen bg-[#1C1C1A] flex items-center justify-center px-4 py-16"
      data-testid="not-found-page"
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-[720px] mx-auto text-center w-full"
      >
        <p
          className="font-bold text-[#C9A84C] mb-6"
          style={{ fontSize: '6rem', lineHeight: 1 }}
          data-testid="not-found-status"
        >
          404
        </p>
        <h1 className="text-2xl font-semibold text-[#E8E8E4] mb-3">
          Questa pagina non esiste.
        </h1>
        <p className="text-base text-[#888780] mb-10">
          Ma la tua educazione finanziaria sì. Prova uno dei nostri strumenti più usati:
        </p>

        {/* Top 3 calculators grid */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10" data-testid="not-found-calculators">
          {TOP_CALCULATORS.map((calc) => (
            <Link
              key={calc.slug}
              to={`/calcolatori/${calc.slug}`}
              data-testid={`not-found-calc-${calc.slug}`}
              className="group block bg-[#252523] border border-[#3A3A37] hover:border-[#C9A84C] rounded-lg p-5 text-left transition-colors"
            >
              <div className="text-[#C9A84C] font-semibold mb-1 group-hover:text-[#D9B85C]">
                {calc.label}
              </div>
              <div className="text-sm text-[#888780]">{calc.desc}</div>
            </Link>
          ))}
        </div>

        <div className="flex gap-3 justify-center flex-wrap">
          <Link
            to="/"
            className="sapiens-btn-primary inline-flex items-center gap-2 px-8 py-3"
            data-testid="not-found-home-btn"
          >
            Torna alla home →
          </Link>
          <Link
            to="/calcolatori"
            className="inline-flex items-center gap-2 px-8 py-3 border border-[#3A3A37] text-[#E8E8E4] rounded hover:border-[#C9A84C]"
            data-testid="not-found-calculators-btn"
          >
            Tutti i calcolatori
          </Link>
        </div>
      </motion.div>
    </div>
  );
};

export default NotFoundPage;
